import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, errorMessageFrom } from '../../api/client';
import {
  setLanguage,
  type PlanContext,
  type PlanTodayResponse,
  type ReadyItem,
} from '../../api/plan';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import { unlockPromptPlayback } from '../../speech';
import {
  allSituations,
  blurbFor,
  type StateFilter,
} from '../../today/todayViewModel';
import ErrorBanner from '../ui/ErrorBanner';
import LoadingState from '../ui/LoadingState';
import { AlsoReady, UpNext } from './UpNext';
import BrowsePanel, { BROWSE_HEADING_ID } from './BrowsePanel';
import LanguageSwitcher from './LanguageSwitcher';
import PickLanguage from './PickLanguage';
import SituationSheet from './SituationSheet';
import TodayHeader from './TodayHeader';
import styles from './TodayScreen.module.css';

type View = 'home' | 'browse';

/**
 * Today (new interface): one next step with its reason, a short list of what else is ready, and every situation
 * browsable by theme. What is ready and why comes from the server (BR-029); this only draws it. Phone: the
 * situation list is its own screen. Wide: it sits beside Today (container query in the CSS).
 */
export const TodayScreen: React.FC = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanTodayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('home');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StateFilter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const allButton = useRef<HTMLButtonElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    let signedOut = false;
    try {
      const response = await apiFetch('/plan/today', {
        signal: controller.signal,
      });
      if (response.status === 401) {
        signedOut = true; // the auth layer is sending the learner to sign in; keep the loading state
        return;
      }
      if (!response.ok) throw new Error(await errorMessageFrom(response));
      setPlan((await response.json()) as PlanTodayResponse);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(
        err instanceof Error ? err.message : "Failed to load today's plan"
      );
    } finally {
      if (!controller.signal.aborted && !signedOut) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return (): void => abortRef.current?.abort();
  }, [load]);

  /** Make a language current, then show Today for it from the top; rejects if the switch fails. */
  const chooseLanguage = async (locale: string): Promise<void> => {
    await setLanguage(locale);
    setView('home');
    setQuery('');
    setFilter('all');
    setOpenId(null);
    await load();
  };

  const showBrowse = (): void => {
    setView('browse');
    // Focus follows the screen change on a phone, where Today is replaced.
    setTimeout(() => document.getElementById(BROWSE_HEADING_ID)?.focus(), 0);
  };
  const showHome = (): void => {
    setView('home');
    setTimeout(() => allButton.current?.focus(), 0);
  };

  const startLesson = (lessonId: string): void => {
    void navigate(`/lesson/${lessonId}`);
  };
  const startCheckIn = (contextId: string): void => {
    unlockPromptPlayback(); // this tap lets the check-in's lines play by themselves
    void navigate(`/assessment/${contextId}`);
  };
  const startItem = (item: ReadyItem): void => {
    if (item.kind === 'review') void navigate('/practice');
    else if (item.kind === 'check_in' && item.context_id)
      startCheckIn(item.context_id);
    else if (item.lesson_id) startLesson(item.lesson_id);
  };

  if (loading && !plan) return <LoadingState variant="full-page" />;
  if (error) {
    return (
      <ErrorBanner
        message={error}
        variant="full-page"
        onRetry={() => void load()}
      />
    );
  }
  if (
    plan &&
    plan.current_locale === null &&
    (plan.available_languages ?? []).length > 0
  ) {
    return (
      <PickLanguage
        available={plan.available_languages ?? []}
        onChoose={chooseLanguage}
      />
    );
  }
  if (!plan || plan.tracks.length === 0) {
    return (
      <ErrorBanner message={INTERFACE_TEXT.tdNoTracks} variant="full-page" />
    );
  }

  const situations = allSituations(plan.tracks);
  const opened: PlanContext | undefined = situations.find(
    (c) => c.context_id === openId
  );
  const upNext = plan.up_next ?? null;

  return (
    <div className={styles.root} data-view={view}>
      <TodayHeader
        switcher={
          <LanguageSwitcher
            languages={plan.languages ?? []}
            available={plan.available_languages ?? []}
            onChoose={chooseLanguage}
          />
        }
      />
      <div className={styles.body}>
        <main className={styles.home}>
          <p className={styles.eyebrow}>{INTERFACE_TEXT.tdEyebrowToday}</p>
          {upNext ? (
            <UpNext
              item={upNext}
              blurb={blurbFor(upNext, situations)}
              onStart={startItem}
            />
          ) : (
            <p className={styles.finished}>
              {plan.all_complete
                ? INTERFACE_TEXT.tdFinished
                : INTERFACE_TEXT.tdNothingReady}
            </p>
          )}
          <AlsoReady items={plan.also_ready ?? []} onStart={startItem} />
          <button
            ref={allButton}
            type="button"
            className={styles.all}
            onClick={showBrowse}
          >
            {INTERFACE_TEXT.tdAllSituations}
            <span>{situations.length} →</span>
          </button>
        </main>
        <section
          className={styles.browse}
          aria-label={INTERFACE_TEXT.tdAllSituations}
        >
          <BrowsePanel
            situations={situations}
            query={query}
            filter={filter}
            onQuery={setQuery}
            onFilter={setFilter}
            onOpen={(context) => setOpenId(context.context_id)}
            onBack={showHome}
          />
        </section>
      </div>
      {opened && (
        <SituationSheet
          context={opened}
          onClose={() => setOpenId(null)}
          onStartLesson={startLesson}
          onStartCheckIn={startCheckIn}
        />
      )}
    </div>
  );
};

export default TodayScreen;
